import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import GlobalProgressView from './GlobalProgressView';
import { getStatusText } from '../../constants/status';
import { ANIMATION_DURATIONS } from '../../constants/animations';

const GlobalProgress = ({ status, onBusyChange, onTextVisible }) => {
  const [animationClass, setAnimationClass] = useState('');
  const [displayText, setDisplayText] = useState('');
  const [displayStatusType, setDisplayStatusType] = useState(null);
  const [textFadeClass, setTextFadeClass] = useState('');
  const [internalStatus, setInternalStatus] = useState(status);
  const [canShowText, setCanShowText] = useState(false);

  const textDisplayTimeRef = useRef(null);
  const lastBusyStateRef = useRef(!!status);
  const isInitialMountRef = useRef(true);

  // Aguarda fade-out do conteúdo + animação de altura antes de mostrar texto
  useEffect(() => {
    const timeout = setTimeout(() => {
      setCanShowText(true);
    }, ANIMATION_DURATIONS.BASE * 2); // 1300ms (650ms fade-out + 650ms altura)

    return () => clearTimeout(timeout);
  }, []);

  // Função helper para notificar mudança de busy
  const notifyBusyChange = (isBusy) => {
    // Não notifica no mount inicial se já está busy
    if (isInitialMountRef.current && isBusy) {
      isInitialMountRef.current = false;
      lastBusyStateRef.current = isBusy;
      return;
    }

    isInitialMountRef.current = false;

    if (onBusyChange && lastBusyStateRef.current !== isBusy) {
      lastBusyStateRef.current = isBusy;
      onBusyChange(isBusy);
    }
  };

  // Processa mudanças de status
  useEffect(() => {
    // Se não há status, limpa
    if (!status) {
      if (displayText) {
        // Se há texto sendo exibido, faz fade-out
        setTextFadeClass('fade-out');

        setTimeout(() => {
          setDisplayText('');
          setDisplayStatusType(null);
          setTextFadeClass('');
          setInternalStatus(null);
          textDisplayTimeRef.current = null;
          notifyBusyChange(false);
        }, ANIMATION_DURATIONS.BASE); // 650ms
      } else {
        // Não há texto, limpa imediatamente
        setInternalStatus(null);
        notifyBusyChange(false);
      }
      return;
    }

    // Há status, processa
    if (!canShowText) {
      // Ainda não pode mostrar texto, aguarda
      setInternalStatus(status);
      notifyBusyChange(true);
      return;
    }

    const newText = getStatusText(status) || 'Carregando...';

    // Se é o mesmo texto, não faz nada
    if (displayText === newText) {
      return;
    }

    setInternalStatus(status);
    notifyBusyChange(true);

    // Se já há texto, faz fade-out primeiro
    if (displayText) {
      setTextFadeClass('fade-out');

      setTimeout(() => {
        setDisplayText(newText);
        setDisplayStatusType(status.type);
        setTextFadeClass('fade-in');
        textDisplayTimeRef.current = Date.now();
      }, ANIMATION_DURATIONS.BASE); // 650ms
    } else {
      // Primeiro texto, apenas fade-in
      setDisplayText(newText);
      setDisplayStatusType(status.type);
      setTextFadeClass('fade-in');
      textDisplayTimeRef.current = Date.now();
    }
  }, [status, canShowText, displayText]);

  // Troca fade-in para visible após completar animação
  useEffect(() => {
    if (textFadeClass === 'fade-in') {
      const timeout = setTimeout(() => {
        setTextFadeClass('visible');
        // Notifica que o texto está visível
        if (onTextVisible) {
          onTextVisible();
        }
      }, ANIMATION_DURATIONS.BASE); // 650ms
      return () => clearTimeout(timeout);
    }
  }, [textFadeClass, onTextVisible]);

  // Controla animação de fade-in/fade-out baseado em ter ou não status interno
  useEffect(() => {
    if (internalStatus) {
      setAnimationClass('fade-in');
    } else {
      setAnimationClass('fade-out');
    }
  }, [internalStatus]);

  return (
    <GlobalProgressView
      animationClass={animationClass}
      displayText={displayText}
      textFadeClass={textFadeClass}
      statusType={displayStatusType}
    />
  );
};

GlobalProgress.propTypes = {
  status: PropTypes.shape({
    type: PropTypes.string.isRequired,
    text: PropTypes.string,
  }),
  onBusyChange: PropTypes.func,
  onTextVisible: PropTypes.func,
};

export default GlobalProgress;
